import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import {
  TARIFIERUNGEN,
  type TarifierungIconKey,
  type TarifierungKey,
  type VertragsartKey,
} from "./types";

const useStyles = makeStyles({
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
    "@media (min-width: 600px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  tile: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.cardBg,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    rowGap: "10px",
    paddingTop: "20px",
    paddingBottom: "18px",
    paddingLeft: "12px",
    paddingRight: "12px",
    minHeight: "168px",
    textAlign: "center",
    ...shorthands.border("2px", "solid", BRAND.border),
    ...shorthands.borderRadius("12px"),
    transitionProperty: "border-color, box-shadow, transform, background-color",
    transitionDuration: "140ms",
    ":hover": {
      ...shorthands.borderColor("rgba(230,106,44,0.6)"),
      transform: "translateY(-2px)",
    },
  },
  tileActive: {
    ...shorthands.borderColor(BRAND.pillarOrange),
    backgroundColor: "rgba(230,106,44,0.06)",
    boxShadow: "0 0 0 4px rgba(230,106,44,0.18)",
  },
  iconWrap: {
    color: BRAND.pillarOrange,
    width: "72px",
    height: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: { fontSize: "1rem", fontWeight: 700, color: BRAND.ink, lineHeight: 1.2 },
  desc: { fontSize: "0.78rem", color: BRAND.muted, lineHeight: 1.3 },
});

const Svg = ({ children }: { children: React.ReactNode }) => (
  <svg
    width="72"
    height="60"
    viewBox="0 0 72 60"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const Icons: Record<TarifierungIconKey, () => JSX.Element> = {
  standard: () => (
    <Svg>
      {/* Single contract sheet with checkmark */}
      <rect x="14" y="6" width="44" height="48" rx="3" />
      <path d="M22 18h28M22 26h22M22 34h26" />
      <circle cx="50" cy="44" r="8" fill="currentColor" stroke="none" opacity="0.85" />
      <path d="M46 44l3 3 5-5" stroke="#fff" strokeWidth="2" />
    </Svg>
  ),
  rahmen: () => (
    <Svg>
      {/* Two-party handshake / agreement: doc + linked rings */}
      <rect x="6" y="6" width="32" height="48" rx="3" />
      <path d="M14 18h18M14 26h16M14 34h18" />
      <circle cx="50" cy="34" r="10" />
      <circle cx="60" cy="34" r="10" />
      <path d="M40 50l8-6M68 50l-8-6" />
    </Svg>
  ),
};

type Props = {
  value: TarifierungKey;
  onChange: (value: TarifierungKey) => void;
  vertragsart: VertragsartKey | "";
};

const TarifierungPicker = ({ value, onChange, vertragsart }: Props) => {
  const styles = useStyles();
  const items = vertragsart
    ? TARIFIERUNGEN.filter((t) => t.availableFor.includes(vertragsart))
    : TARIFIERUNGEN;
  return (
    <div className={styles.grid}>
      {items.map((t) => {
        const Render = Icons[t.iconKey];
        const active = value === t.key;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={mergeClasses(styles.tile, active && styles.tileActive)}
            aria-pressed={active}
          >
            <span className={styles.iconWrap}>
              <Render />
            </span>
            <span className={styles.title}>{t.label}</span>
            <span className={styles.desc}>{t.description}</span>
          </button>
        );
      })}
    </div>
  );
};

export default TarifierungPicker;
